import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout";
import MapModal from "./MapModal";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost/securecontrol-backend";

const COLORS = {
  primary: "#0B3C5D",
  secondary: "#1E88C8",
  accent: "#4FC3F7",
  darkText: "#2E2E2E",
  lightBg: "#F5F7FA",
  white: "#FFFFFF",
  danger: "#f44336",
  success: "#4caf50",
  warning: "#ff9800",
  info: "#2196f3",
  successLight: "#e8f5e9",
  dangerLight: "#ffebee",
  warningLight: "#fff3e0",
  infoLight: "#e3f2fd",
};

const statusLabel = (statut) => {
  switch (statut) {
    case 1:
      return {
        text: "En cours",
        style: { backgroundColor: "#fff3e0", color: "#f57c00" },
      };
    case 2:
      return {
        text: "Terminée",
        style: { backgroundColor: "#e8f5e9", color: "#2e7d32" },
      };
    default:
      return {
        text: "Inconnu",
        style: { backgroundColor: "#F5F7FA", color: "#6c757d" },
      };
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
};

const RoundDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ronde, setRonde] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showMap, setShowMap] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUserInfo({
          ...userData,
          profile: userData.profile || userData.poste || 0,
          societe_id: userData.societe_id || 0,
        });
      } catch {
        navigate("/login");
      }
    }
  }, [navigate]);

  const fetchRondeDetail = async () => {
    if (!id || isNaN(parseInt(id))) {
      setError("ID de ronde invalide");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        setLoading(false);
        return;
      }
      const response = await axios.get(
        `${API_BASE_URL}/rondes/detail_rondes.php?id=${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
          timeout: 10000,
        },
      );
      if (response.data?.success && response.data.data) {
        setRonde(response.data.data);
      } else {
        setError("Format de réponse inattendu de l'API.");
      }
    } catch (err) {
      let errorMsg = "Erreur lors du chargement des détails.";
      if (err.response) {
        if (err.response.data?.message) {
          errorMsg = err.response.data.message;
        } else {
          switch (err.response.status) {
            case 400:
              errorMsg = "ID de ronde invalide.";
              break;
            case 401:
              errorMsg = "Session expirée. Veuillez vous reconnecter.";
              localStorage.removeItem("access_token");
              setTimeout(() => navigate("/login"), 2000);
              break;
            case 403:
              errorMsg = "Accès interdit.";
              break;
            case 404:
              errorMsg = "Ronde non trouvée.";
              break;
            case 500:
              errorMsg = "Erreur serveur.";
              break;
            default:
              errorMsg = `Erreur ${err.response.status}`;
          }
        }
      } else if (err.request) {
        errorMsg = "Serveur inaccessible.";
      } else {
        errorMsg = "Erreur: " + err.message;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRondeDetail();
  }, [id]);

  const handleShowMap = () => {
    if (
      !ronde?.sites ||
      ronde.sites.filter((s) => s.latitude && s.longitude).length === 0
    ) {
      alert("Aucun site avec coordonnées GPS pour cette ronde.");
      return;
    }
    setShowMap(true);
  };

  const scannedCount = ronde?.sites?.filter((site) => site.scanne).length || 0;
  const totalSites = ronde?.sites?.length || 0;

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: "600" }}>
            Détails de la ronde
          </h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>
                  Tableau de bord
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/rondes" style={{ color: COLORS.primary }}>
                  Rondes
                </Link>
              </li>
              <li
                className="breadcrumb-item active"
                style={{ color: COLORS.secondary }}
              >
                Détails
              </li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div
            className="card"
            style={{
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            }}
          >
            <div className="card-body text-center py-5">
              <div
                className="spinner-border"
                role="status"
                style={{ color: COLORS.primary }}
              />
              <p className="mt-2" style={{ color: COLORS.darkText }}>
                Chargement des détails...
              </p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: "600" }}>
            Détails de la ronde
          </h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>
                  Tableau de bord
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/rondes" style={{ color: COLORS.primary }}>
                  Rondes
                </Link>
              </li>
              <li
                className="breadcrumb-item active"
                style={{ color: COLORS.secondary }}
              >
                Erreur
              </li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div
            className="card"
            style={{
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            }}
          >
            <div className="card-body">
              <div
                className="alert alert-danger d-flex align-items-center"
                style={{
                  backgroundColor: "#ffebee",
                  borderColor: COLORS.danger,
                  color: "#c62828",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${COLORS.danger}`,
                }}
              >
                <i className="bi bi-exclamation-triangle me-2"></i>
                <div className="flex-grow-1">{error}</div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError("")}
                />
              </div>
              <div className="text-center">
                <button
                  className="btn fw-semibold"
                  onClick={() => navigate("/rondes")}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: "transparent",
                    borderRadius: "8px",
                    padding: "10px 24px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = "white";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i> Retour aux rondes
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (!ronde) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: "600" }}>
            Détails de la ronde
          </h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>
                  Tableau de bord
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/rondes" style={{ color: COLORS.primary }}>
                  Rondes
                </Link>
              </li>
              <li
                className="breadcrumb-item active"
                style={{ color: COLORS.secondary }}
              >
                Détails
              </li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div
            className="card"
            style={{
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            }}
          >
            <div className="card-body text-center py-5">
              <div
                className="alert alert-warning d-flex align-items-center"
                style={{
                  backgroundColor: "#fff3e0",
                  borderColor: COLORS.warning,
                  color: "#f57c00",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${COLORS.warning}`,
                }}
              >
                <i className="bi bi-exclamation-triangle me-2"></i>
                <div>Impossible de charger les détails de la ronde.</div>
              </div>
              <button
                className="btn fw-semibold"
                onClick={() => navigate("/rondes")}
                style={{
                  color: COLORS.primary,
                  borderColor: COLORS.primary,
                  backgroundColor: "transparent",
                  borderRadius: "8px",
                  padding: "10px 24px",
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = COLORS.primary;
                  e.target.style.color = "white";
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.color = COLORS.primary;
                }}
              >
                <i className="bi bi-arrow-left me-2"></i> Retour aux rondes
              </button>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  const statut = statusLabel(ronde.statut);

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: "600" }}>
          Détails de la ronde
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>
                Tableau de bord
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/rondes" style={{ color: COLORS.primary }}>
                Rondes
              </Link>
            </li>
            <li
              className="breadcrumb-item active"
              style={{ color: COLORS.secondary }}
            >
              Ronde {ronde.id}
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div
          className="card"
          style={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            border: "none",
          }}
        >
          <div className="card-body">
            {/* Titre */}
            <h5
              className="card-title mb-4"
              style={{
                color: COLORS.primary,
                borderBottom: `2px solid ${COLORS.lightBg}`,
                paddingBottom: "16px",
              }}
            >
              <div className="d-flex align-items-center">
                <div
                  className="rounded-circle d-inline-flex align-items-center justify-content-center me-3"
                  style={{
                    width: "36px",
                    height: "36px",
                    backgroundColor: "#E3F2FD",
                    color: COLORS.primary,
                  }}
                >
                  <i className="bi bi-shield-check"></i>
                </div>
                {ronde.titre ? `Ronde : ${ronde.titre}` : `Ronde #${ronde.id}`}
              </div>
            </h5>

            {/* Dates + Statut */}
            <div className="row mb-4 g-3">
              <div className="col-md-4">
                <div
                  className="card h-100"
                  style={{
                    backgroundColor: COLORS.white,
                    border: "none",
                    borderRadius: "10px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  }}
                >
                  <div className="card-body">
                    <small
                      className="text-uppercase"
                      style={{ color: COLORS.primary, fontWeight: "500" }}
                    >
                      Date de début
                    </small>
                    <p
                      className="mb-0 fw-bold"
                      style={{ fontSize: "1.1rem", color: COLORS.darkText }}
                    >
                      {formatDate(ronde.date_ronde)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div
                  className="card h-100"
                  style={{
                    backgroundColor: COLORS.white,
                    border: "none",
                    borderRadius: "10px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  }}
                >
                  <div className="card-body">
                    <small
                      className="text-uppercase"
                      style={{ color: COLORS.primary, fontWeight: "500" }}
                    >
                      Date de fin
                    </small>
                    <p
                      className="mb-0 fw-bold"
                      style={{ fontSize: "1.1rem", color: COLORS.darkText }}
                    >
                      {ronde.date_fin ? formatDate(ronde.date_fin) : "-"}
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div
                  className="card h-100"
                  style={{
                    backgroundColor: COLORS.white,
                    border: "none",
                    borderRadius: "10px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  }}
                >
                  <div className="card-body">
                    <small
                      className="text-uppercase"
                      style={{ color: COLORS.primary, fontWeight: "500" }}
                    >
                      Statut
                    </small>
                    <p className="mb-0">
                      <span
                        className="badge fw-semibold"
                        style={{
                          ...statut.style,
                          padding: "8px 16px",
                          borderRadius: "6px",
                          fontSize: "1rem",
                        }}
                      >
                        {statut.text}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contrôleur + Scans */}
            <div className="row mb-4">
              <div className="col-md-6">
                {ronde.controleur && (
                  <div
                    className="card h-100"
                    style={{
                      border: `1px solid ${COLORS.lightBg}`,
                      borderRadius: "10px",
                    }}
                  >
                    <div
                      className="card-header"
                      style={{
                        backgroundColor: COLORS.lightBg,
                        borderBottom: `1px solid ${COLORS.lightBg}`,
                        padding: "12px 16px",
                      }}
                    >
                      <h6
                        className="card-title mb-0"
                        style={{ color: COLORS.primary, fontWeight: "600" }}
                      >
                        <i
                          className="bi bi-shield-check me-2"
                          style={{ color: COLORS.secondary }}
                        ></i>
                        Contrôleur
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="me-3">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                            style={{
                              width: "60px",
                              height: "60px",
                              fontSize: "20px",
                              backgroundColor: "#fff3e0",
                              color: "#f57c00",
                            }}
                          >
                            {ronde.controleur.nom_complet?.charAt(0) || "C"}
                          </div>
                        </div>
                        <div>
                          <h5 className="mb-1">
                            {ronde.controleur.nom_complet}
                          </h5>
                          {ronde.controleur.email && (
                            <p className="mb-1">
                              <i className="bi bi-envelope me-1 text-muted"></i>
                              {ronde.controleur.email}
                            </p>
                          )}
                          {ronde.controleur.telephone && (
                            <p className="mb-0">
                              <i className="bi bi-telephone me-1 text-muted"></i>
                              {ronde.controleur.telephone}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <div
                  className="card h-100"
                  style={{
                    border: `1px solid ${COLORS.lightBg}`,
                    borderRadius: "10px",
                    background:
                      "linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%)",
                  }}
                >
                  <div className="card-body d-flex flex-column justify-content-center">
                    <h6
                      className="card-title mb-3"
                      style={{ color: COLORS.primary, fontWeight: "600" }}
                    >
                      <i
                        className="bi bi-qr-code-scan me-2"
                        style={{ color: COLORS.secondary }}
                      ></i>
                      Sites scannés
                    </h6>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-semibold">Progression</span>
                      <span
                        className="badge"
                        style={{
                          backgroundColor: COLORS.infoLight,
                          color: COLORS.info,
                          padding: "4px 8px",
                        }}
                      >
                        {scannedCount} / {totalSites}
                      </span>
                    </div>
                    <div
                      className="progress mb-3"
                      style={{
                        height: "12px",
                        borderRadius: "6px",
                        backgroundColor: COLORS.lightBg,
                      }}
                    >
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{
                          width: `${(scannedCount / (totalSites || 1)) * 100}%`,
                          backgroundColor: COLORS.success,
                        }}
                      />
                    </div>
                    <p className="text-muted small mb-0">
                      {scannedCount === totalSites
                        ? "Tous les sites ont été scannés."
                        : `${totalSites - scannedCount} site(s) restant(s) à scanner.`}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Liste des sites */}
            <h5
              className="mt-4 mb-3"
              style={{ color: COLORS.primary, fontWeight: "600" }}
            >
              <i className="bi bi-building me-2"></i>Les sites de la ronde
            </h5>

            {ronde.sites && ronde.sites.length > 0 ? (
              <div className="row">
                {ronde.sites.map((site) => (
                  <div key={site.id} className="col-lg-6 mb-4">
                    <div
                      className="card h-100"
                      style={{
                        border: `1px solid ${COLORS.lightBg}`,
                        borderRadius: "10px",
                      }}
                    >
                      <div
                        className="card-header d-flex justify-content-between align-items-center"
                        style={{
                          backgroundColor: COLORS.lightBg,
                          borderBottom: `1px solid ${COLORS.lightBg}`,
                          padding: "12px 16px",
                        }}
                      >
                        <h6
                          className="card-title mb-0"
                          style={{ color: COLORS.primary, fontWeight: "600" }}
                        >
                          <i
                            className="bi bi-building me-2"
                            style={{ color: COLORS.secondary }}
                          ></i>
                          {site.nom}
                        </h6>
                        {site.scanne ? (
                          <span
                            className="badge"
                            style={{
                              backgroundColor: COLORS.successLight,
                              color: COLORS.success,
                            }}
                          >
                            <i className="bi bi-check-circle me-1"></i> Scanné
                          </span>
                        ) : (
                          <span
                            className="badge"
                            style={{
                              backgroundColor: COLORS.dangerLight,
                              color: COLORS.danger,
                            }}
                          >
                            <i className="bi bi-x-circle me-1"></i> Non scanné
                          </span>
                        )}
                      </div>
                      <div className="card-body">
                        <p className="mb-2">
                          <i className="bi bi-geo-alt me-1 text-muted"></i>
                          {site.adresse || "Adresse non disponible"}
                        </p>
                        {site.client && (
                          <div
                            className="mt-3 p-3"
                            style={{
                              backgroundColor: "#e3f2fd",
                              borderRadius: "8px",
                              borderLeft: `4px solid ${COLORS.primary}`,
                            }}
                          >
                            <small
                              className="text-uppercase"
                              style={{
                                color: COLORS.primary,
                                fontWeight: "600",
                              }}
                            >
                              Client
                            </small>
                            <p
                              className="mb-1 fw-bold"
                              style={{ fontSize: "1.1rem" }}
                            >
                              {site.client.nom_complet}
                            </p>
                            {site.client.email && (
                              <small>
                                <i className="bi bi-envelope me-1"></i>
                                {site.client.email}
                              </small>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-muted">Aucun site associé à cette ronde.</p>
              </div>
            )}

            {/* Bouton carte */}
            {ronde.sites &&
              ronde.sites.filter((s) => s.latitude && s.longitude).length >
                0 && (
                <div className="text-center mt-3">
                  <button
                    className="btn fw-semibold"
                    onClick={handleShowMap}
                    style={{
                      color: COLORS.info,
                      borderColor: COLORS.info,
                      backgroundColor: "transparent",
                      borderRadius: "8px",
                      padding: "10px 24px",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = COLORS.info;
                      e.target.style.color = "white";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "transparent";
                      e.target.style.color = COLORS.info;
                    }}
                  >
                    <i className="bi bi-map me-2"></i> Voir tous les sites sur
                    la carte
                  </button>
                </div>
              )}
          </div>
        </div>
      </section>

      {/* MapModal */}
      {showMap && ronde && (
        <MapModal
          isOpen={showMap}
          onClose={() => setShowMap(false)}
          latitude={ronde.sites.find((s) => s.latitude)?.latitude || null}
          longitude={ronde.sites.find((s) => s.longitude)?.longitude || null}
          title={`Sites de la ronde #${ronde.id}`}
          siteName=""
          agentName=""
          date=""
          rondeStatus=""
          allRondes={ronde.sites
            .filter((s) => s.latitude && s.longitude)
            .map((s) => ({
              latitude: s.latitude,
              longitude: s.longitude,
              titre: s.nom,
              site: { nom: s.nom },
              statut: ronde.statut,
              scanne: s.scanne,
            }))}
          showAllRondes={true}
          initialZoom={10}
        />
      )}
    </Layout>
  );
};

export default RoundDetail;
